import React, { useEffect, useState } from "react";
import styles from "./journey.module.css";
import { useNavigate } from "react-router-dom";
import ContainerHeader from "common/ContainerHeader";
import { deleteData, getAll, getData, updateData } from "utils/queries";
import IconButton from "common/IconButton";
import Badge from "common/Badge";
import SelectSearch from "common/SelectSearch";
import { SearchOptions } from "utils/queries";

const Journey = () => {
  const [data, setData] = useState();
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedOverJourney, setDraggedOverJourney] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const navigate = useNavigate();
  
  const fetchAllPackages = async () => {
    try {
      let query = `journey/all`;
      
      // Add event_id filter if event is selected
      if (selectedEvent?.value) {
        query += `?event_id=${selectedEvent.value}`;
      }
      
      const result = await getAll(query);
      const Data = result?.data?.data;
      if (Data) {
        // Sort packages by priority for each journey
        const sortedData = Data.map(journey => ({
          ...journey,
          packages: (journey.packages || []).filter(p => p).sort((a, b) => (a.priority || 0) - (b.priority || 0))
        }));
        setData(sortedData);
      }
    } catch (error) {
      console.error("Error fetching journeys:", error);
    }
  };

  useEffect(() => {
    fetchAllPackages();
  }, [selectedEvent?.value]);

  const fetchOptions = async (search = "") => {
    try {
      let optionsResult;
      
      if (!search || search.trim() === "") {
        // Load all events (past and upcoming) when no search term
        optionsResult = await SearchOptions(`event/events?limit=100&offset=0`);
        return optionsResult?.data?.data?.rows?.map((e) => ({
          value: e?.id,
          label: e?.event_name,
        })) || [];
      } else {
        // Use search endpoint when user types
        optionsResult = await SearchOptions(`ticket/search?key=${search}`);
        return optionsResult?.data?.data
          ?.filter((e) =>
            e?.event_name?.toLowerCase().includes(search.toLowerCase())
          )
          ?.map((e) => ({ value: e?.id, label: e?.event_name })) || [];
      }
    } catch (error) {
      console.error("Error fetching options:", error);
      return [];
    }
  };

  const deleteJourney = async (event, id) => {
    event.stopPropagation(); 
    event.preventDefault(); 
    try {
      const result = await deleteData(`journey/${id}`);
      fetchAllPackages();
      return result;
    } catch (error) {
      console.error("Error deleting journey:", error);
    }
  };

  const handleDragStart = (e, packageItem, journeyId) => {
    setDraggedItem({ package: packageItem, journeyId });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, journeyId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDraggedOverJourney(journeyId);
  };

  const handleDragLeave = () => {
    setDraggedOverJourney(null);
  };

  const handleDrop = async (e, targetJourneyId, targetIndex) => {
    e.preventDefault();
    
    if (!draggedItem) return;
    
    const { package: draggedPackage, journeyId: sourceJourneyId } = draggedItem;
    
    // Only allow reordering within the same journey
    if (sourceJourneyId !== targetJourneyId) {
      setDraggedItem(null);
      setDraggedOverJourney(null);
      return;
    }

    // Find the journey and reorder packages
    const updatedData = data.map(journey => {
      if (journey.id === targetJourneyId) {
        const packages = [...journey.packages];
        const draggedIndex = packages.findIndex(pkg => 
          pkg.variant_id === draggedPackage.variant_id && 
          pkg.package_id === draggedPackage.package_id
        );
        
        if (draggedIndex === -1 || draggedIndex === targetIndex) return journey;
        
        // Remove dragged item and insert at new position
        const [removed] = packages.splice(draggedIndex, 1);
        packages.splice(targetIndex, 0, removed);
        
        // Update priorities based on new order
        const updatedPackages = packages.map((pkg, index) => ({
          ...pkg,
          priority: index + 1
        }));

        return { ...journey, packages: updatedPackages };
      }
      return journey;
    });

    setData(updatedData);
    
    // Update the journey with new order
    try {
      const journeyToUpdate = updatedData.find(j => j.id === targetJourneyId);
      await updateJourneyOrder(journeyToUpdate);
    } catch (error) {
      console.error("Error updating journey order:", error);
      // Revert on error
      fetchAllPackages();
    }
    
    setDraggedItem(null);
    setDraggedOverJourney(null);
  };


// Update the updateJourneyOrder function to fetch description before updating
const updateJourneyOrder = async (journey) => {
  try {
    // First, fetch the complete journey details to get the description
    const journeyDetails = await getData(`journey/${journey.id}`, false);
    const currentDescription = journeyDetails?.data?.data?.description || "";
    
    // Prepare the data structure for the API
    const tickets = [];
    const packages = [];
    const addOns = [];

    journey.packages.forEach(pkg => {
      const baseData = {
        priority: pkg.priority
      };

      if (pkg.type === 'ticket') {
        tickets.push({
          id: pkg.ticket_id,
          ticket_name: pkg.name,
          variants: [{
            variant_id: pkg.variant_id,
            variant_name: pkg.name,
            checked: true,
            priority: pkg.priority
          }]
        });
      } else if (pkg.type === 'package') {
        packages.push({
          package_id: pkg.package_id,
          package_name: pkg.name,
          priority: pkg.priority
        });
      } else if (pkg.type === 'add_on') {
        addOns.push({
          id: pkg.ticket_id,
          ticket_name: pkg.name,
          variants: [{
            variant_id: pkg.variant_id,
            variant_name: pkg.name,
            checked: true,
            priority: pkg.priority
          }]
        });
      }
    });

    const updatePayload = {
      journey_name: journey.name,
      journey_image: journey.image_url,
      description: currentDescription, // Use the fetched description
      tickets,
      packages,
      add_ons: addOns
    };
    await updateData(`journey/${journey.id}`, updatePayload);
  } catch (error) {
    console.error("Error fetching journey description:", error);
    throw error; // Re-throw to handle in the calling function
  }
};

  const renderDragHandle = () => (
    <div className={styles.dragHandle}>
      {/* <svg width="12" height="20" viewBox="0 0 12 20" fill="none">
        <circle cx="2" cy="4" r="1.5" fill="#999" />
        <circle cx="2" cy="10" r="1.5" fill="#999" />
        <circle cx="2" cy="16" r="1.5" fill="#999" />
        <circle cx="10" cy="4" r="1.5" fill="#999" />
        <circle cx="10" cy="10" r="1.5" fill="#999" />
        <circle cx="10" cy="16" r="1.5" fill="#999" />
      </svg> */}
    </div>
  );

  return (
    <>
      <div className={styles.container}>
        <ContainerHeader
          label="Create Ticket Journey"
          showAddButton={false}
        />

        <div className={styles.journeyDetails}>
          <div className={styles.journeyHeaderRow}>
            <div className={styles.includelabels}>Your Ticket Journeys</div>
            <div className={styles.filterWrapper}>
              <SelectSearch
                id="journey-event-filter"
                placeholder="Select Event"
                value={selectedEvent}
                onChange={(val) => setSelectedEvent(val)}
                loadOptions={fetchOptions}
                formGroupStyleOverride={{ width: "25rem", marginBottom: "0" }}
              />
            </div>
          </div>

          {data?.map((journey, journeyIndex) => {
            return (
              <div key={journey.id} className={styles.includedItems}>
                <div className={styles.includes}>
                  <div className={styles.includesElements}>
                    {journey?.image_url ? (
                      <img
                        className={styles?.ticket_image}
                        src={journey?.image_url}
                        alt={journey?.name}
                      />
                    ) : (
                      <div className={styles?.ticket_image} />
                    )}
                    <div className={styles.info}>
                      <div className={styles.label}>{journey?.name}</div>
                      <div 
                        className={styles.variantTicets}
                        onDragOver={(e) => handleDragOver(e, journey.id)}
                        onDragLeave={handleDragLeave}
                      >
                        {journey?.packages?.filter(p => p && (p.name || p.ticket_name)).map((pkg, packageIndex) => {
                          const isDragging = draggedItem?.package?.variant_id === pkg.variant_id && 
                                           draggedItem?.package?.package_id === pkg.package_id &&
                                           draggedItem?.package?.type === pkg.type;
                          const isOverJourney = draggedOverJourney === journey.id;
                          
                          return (
                            <div
                              key={`${pkg.type}-${pkg.package_id || pkg.variant_id}-${packageIndex}`}
                              className={`${styles.draggableBadge} ${isDragging ? styles.dragging : ''} ${isOverJourney ? styles.dragOver : ''}`}
                              draggable
                              onDragStart={(e) => handleDragStart(e, pkg, journey.id)}
                              onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onDrop={(e) => {
                                e.stopPropagation();
                                handleDrop(e, journey.id, packageIndex);
                              }}
                            >
                              {renderDragHandle()}
                              <Badge 
                                label={pkg?.name || journey?.name}
                                badgeClassOverride={styles.draggableBadgeContent}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div className={styles.actionButtons}>
                    <Badge
                      label="Edit"
                      badgeClassOverride={styles.badgeClassOverride}
                      onClick={() => {
                        navigate(`./edit-journey/${journey?.id}`);
                      }}
                    />
                    <Badge
                      label="Delete"
                      badgeClassOverride={styles.badgeClassDelete} 
                      onClick={(event) => deleteJourney(event, journey?.id)}
                    />
                  </div>
                </div>
              </div>
            );
          })}

          <IconButton
            overrideButtonStyles={styles.overrideButtonStyles}
            buttonLabel={"New Ticket Journey"}
            icon={"addBlack"}
            buttonFunction={() => {
              navigate(`./create-journey`);
            }}
          />
        </div>
      </div>
    </>
  );
};

export default Journey;